import apiClient from './apiClient';
import { AxiosResponse } from 'axios';
import { User } from './userService';
import { Session } from './sessionService';

export interface Enrollment {
  id: number;
  sessionId: number;
  userId: number;
  enrollmentDate: string;
  paymentStatus: 'unpaid' | 'pending' | 'completed' | 'failed' | 'refunded';
  createdAt: string;
  updatedAt: string;
}

export interface EnrollmentWithUser extends Enrollment {
  user: User;
}

export interface EnrollmentWithSession extends Enrollment {
  session: Session;
}

export interface EnrollmentWithDetails extends Enrollment {
  user: User;
  session: Session;
}

// User type is now imported from userService

export interface EnrollmentResponse {
  enrollments: EnrollmentWithUser[];
  session: Session;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UserEnrollmentsResponse {
  user: User;
  enrollments: EnrollmentWithSession[];
}

// Get all enrollments with pagination
export const getAllEnrollments = async (page = 1, limit = 20): Promise<EnrollmentResponse> => {
  try {
    const response: AxiosResponse<EnrollmentResponse> = await apiClient.get(
      `/api/enrollments?page=${page}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching enrollments:', error);
    throw error;
  }
};

// Get enrollment by ID
export const getEnrollmentById = async (id: number): Promise<Enrollment> => {
  try {
    const response: AxiosResponse<Enrollment> = await apiClient.get(`/api/enrollments/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching enrollment ${id}:`, error);
    throw error;
  }
};

// Get enrollments by session
export const getEnrollmentsBySession = async (
  sessionId: number,
  page = 1,
  limit = 20
): Promise<EnrollmentResponse> => {
  try {
    const response: AxiosResponse<EnrollmentResponse> = await apiClient.get(
      `/api/enrollments/session/${sessionId}?page=${page}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching enrollments for session ${sessionId}:`, error);
    throw error;
  }
};

// Get enrollments by user
export const getEnrollmentsByUser = async (userId: number): Promise<UserEnrollmentsResponse> => {
  try {
    const response: AxiosResponse<UserEnrollmentsResponse> = await apiClient.get(
      `/api/enrollments/user/${userId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching enrollments for user ${userId}:`, error);
    throw error;
  }
};

// Check if user is enrolled in session
export const checkUserEnrollment = async (userId: number, sessionId: number): Promise<{ isEnrolled: boolean; hasPaid: boolean }> => {
  try {
    const response: AxiosResponse<{ isEnrolled: boolean; hasPaid: boolean }> = await apiClient.get(
      `/api/enrollments/check/${userId}/${sessionId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error checking enrollment for user ${userId} in session ${sessionId}:`, error);
    throw error;
  }
};

// Create a new enrollment
export const createEnrollment = async (
  userId: number,
  sessionId: number,
  enrollmentDate?: Date,
  paymentStatus?: 'unpaid' | 'pending' | 'completed' | 'failed' | 'refunded'
): Promise<Enrollment> => {
  try {
    const response: AxiosResponse<{ message: string; enrollment: Enrollment }> = await apiClient.post(
      '/api/enrollments',
      {
        userId,
        sessionId,
        enrollmentDate: enrollmentDate?.toISOString(),
        paymentStatus
      }
    );
    return response.data.enrollment;
  } catch (error) {
    console.error('Error creating enrollment:', error);
    throw error;
  }
};

// Bulk enroll users
export const bulkEnrollUsers = async (
  sessionId: number,
  userIds: number[],
  paymentStatus?: 'unpaid' | 'pending' | 'completed' | 'failed' | 'refunded'
): Promise<{
  message: string;
  results: { userId: number; status: string; enrollmentId?: number }[];
}> => {
  try {
    const response: AxiosResponse<{
      message: string;
      results: { userId: number; status: string; enrollmentId?: number }[];
    }> = await apiClient.post('/api/enrollments/bulk', {
      sessionId,
      userIds,
      paymentStatus
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk enrolling users:', error);
    throw error;
  }
};

// Delete enrollment
export const deleteEnrollment = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/enrollments/${id}`);
  } catch (error) {
    console.error(`Error deleting enrollment ${id}:`, error);
    throw error;
  }
};

// Delete enrollment by user and session
export const deleteEnrollmentByUserAndSession = async (
  userId: number,
  sessionId: number
): Promise<void> => {
  try {
    await apiClient.delete(`/api/enrollments/user/${userId}/session/${sessionId}`);
  } catch (error) {
    console.error(`Error deleting enrollment for user ${userId} in session ${sessionId}:`, error);
    throw error;
  }
};

// Update enrollment payment status
export const updateEnrollmentPaymentStatus = async (
  enrollmentId: number,
  paymentStatus: 'unpaid' | 'pending' | 'completed' | 'failed' | 'refunded'
): Promise<Enrollment> => {
  try {
    const response: AxiosResponse<{
      message: string;
      enrollment: Enrollment
    }> = await apiClient.patch(
      `/api/enrollments/${enrollmentId}/payment-status`,
      { paymentStatus }
    );
    return response.data.enrollment;
  } catch (error) {
    console.error(`Error updating payment status for enrollment ${enrollmentId}:`, error);
    throw error;
  }
};

// Enroll in free sessions
export const enrollInFreeSessions = async (
  sessionIds: number[],
  discountCode?: string
): Promise<{
  message: string;
  enrollments: Enrollment[];
}> => {
  try {
    const response: AxiosResponse<{
      message: string;
      enrollments: Enrollment[];
    }> = await apiClient.post('/api/enrollments/free', {
      sessionIds,
      ...(discountCode ? { discountCode } : {})
    });
    return response.data;
  } catch (error) {
    console.error('Error enrolling in free sessions:', error);
    throw error;
  }
};